import { ListChecks, ShieldAlert, TrendingUp, LifeBuoy, BookOpen } from "lucide-react";
import { Section, Bullets, SubTitle } from "@/components/analysis/resultUi";
import { VerifiedUse } from "@/components/analysis/ConsultParts";

const ANSWER = { no: "No", solo_se: "Solo se…", si: "Sì" };
const ORIGIN = { spontaneo: "presente spontaneamente", introducibile: "impiegabile con lanci", entrambi: "spontaneo e impiegabile con lanci" };
const P = ({ children, className = "", testid }) => <p className={`text-[17px] leading-relaxed text-[#2D3A30] ${className}`} data-testid={testid}>{children}</p>;

function Actions({ c }) {
  return (
    <Section icon={ListChecks} title="Cosa devo fare ora?" testid="consult2-actions">
      <ol className="space-y-4">
        {c.actions.map((a, i) => (
          <li key={i} className="flex gap-3" data-testid={`consult2-action-${i}`}>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0D2517] text-[15px] font-bold text-[#F7F4EE]">{i + 1}</span>
            <div className="pt-0.5">
              <P className="font-semibold !text-[#0D2517]">{a.what}{a.when && <span className="font-normal text-[#5b6b60]"> · {a.when}</span>}</P>
              {a.how && <P className="mt-1">{a.how}</P>}
            </div>
          </li>
        ))}
      </ol>
      {c.avoid.length > 0 && <><SubTitle>Da evitare</SubTitle><Bullets items={c.avoid} /></>}
    </Section>
  );
}

function Treatment({ t }) {
  return (
    <Section icon={ShieldAlert} title="Serve un prodotto fitosanitario?" testid="consult2-treatment">
      <P className="font-semibold !text-[#0D2517]" testid="consult2-treatment-answer">{ANSWER[t.answer]}</P>
      {t.explanation && <P className="mt-1" testid="consult2-treatment-explanation">{t.explanation}</P>}
      {t.substances.length > 0 && (
        <><SubTitle>Sostanze attive pertinenti</SubTitle>
          <ul className="space-y-2">{t.substances.map((s, i) => (
            <li key={s.iso || i} className="text-[17px] leading-relaxed text-[#2D3A30]" data-testid={`consult2-substance-${i}`}><strong className="text-[#0D2517]">{s.name}</strong>{s.note && `: ${s.note}`}</li>
          ))}</ul></>
      )}
      {t.verified_uses?.length > 0 && <div className="mt-3 space-y-3">{t.verified_uses.map((u, i) => <VerifiedUse key={u.id || i} u={u} i={i} />)}</div>}
      {t.warning && <p className="mt-4 rounded-xl bg-[#f8f3e9] p-3 text-[16px] leading-relaxed text-[#2D3A30]" data-testid="consult2-treatment-warning">⚠ {t.warning}</p>}
    </Section>
  );
}

function Progress({ p }) {
  if (!p.check && !p.better.length && !p.worse.length && !p.reassess) return null;
  return (
    <Section icon={TrendingUp} title="Come capisco se migliora?" testid="consult2-progress">
      {p.check && <P>{p.check}</P>}
      {p.better.length > 0 && <><SubTitle>Migliora se</SubTitle><Bullets items={p.better} /></>}
      {p.worse.length > 0 && <><SubTitle>Peggiora se</SubTitle><Bullets items={p.worse} /></>}
      {p.reassess && <P className="mt-3"><strong>Quando ricontrollare:</strong> {p.reassess}</P>}
    </Section>
  );
}

function Technical({ c, result }) {
  const t = c.technical;
  const alts = result.details?.alternatives || [];
  if (!t.natural_enemies.length && !t.favoring_conditions.length && !t.notes.length && !alts.length) return null;
  return (
    <section className="rounded-2xl border border-dashed border-[#ddc69c] bg-[#fbf8f2] p-5" data-testid="consult2-technical">
      <h2 className="mb-1 flex items-center gap-2 font-serif text-[20px] font-semibold text-[#0D2517]"><BookOpen size={20} className="text-[#B39257]" />Approfondimento tecnico</h2>
      <p className="mb-2 text-[14px] text-[#8a958d]">Per chi vuole più dettagli: le indicazioni per intervenire sono tutte qui sopra.</p>
      {alts.length > 0 && <><SubTitle>Altre cause considerate</SubTitle><Bullets items={alts.map((a) => [a.name, a.against?.[0]].filter(Boolean).join(": "))} /></>}
      {t.natural_enemies.length > 0 && (
        <><SubTitle>Nemici naturali</SubTitle>
          <Bullets items={t.natural_enemies.map((e) => <span key={e.organism}><em>{e.organism}</em>{e.role && ` (${e.role}`}{ORIGIN[e.origin] && `, ${ORIGIN[e.origin]}`}{e.role && ")"}{e.target && ` – contro ${e.target}`}{e.conserve && `. ${e.conserve}`}</span>)} /></>
      )}
      {t.favoring_conditions.length > 0 && <><SubTitle>Fattori che favoriscono il problema</SubTitle><Bullets items={t.favoring_conditions} /></>}
      {t.notes.length > 0 && <><SubTitle>Note tecniche</SubTitle><Bullets items={t.notes} /></>}
    </section>
  );
}

export default function ResultConsultV2({ consult: c, result }) {
  return (
    <div className="space-y-4" data-testid="result-consult-v2">
      <Actions c={c} />
      <Treatment t={c.treatment} />
      <Progress p={c.progress} />
      {c.help && <Section icon={LifeBuoy} title="Quando chiedere aiuto?" testid="consult2-help"><P>{c.help}</P></Section>}
      <Technical c={c} result={result} />
    </div>
  );
}
